import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Sparkles, Loader2, ImageIcon, Video, UserCheck, Plus, Search, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { CommunityCard } from "../components/community/CommunityCard";
import { PostDetailModal } from "../components/community/PostDetailModal";
import { ShareToCommunityButton } from "../components/community/ShareToCommunityButton";
import { WeekendTop20Carousel } from "../components/community/WeekendTop20Carousel";
import { Link, useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 24;

const FILTERS = [
  { id: "all", label: "All", icon: Sparkles },
  { id: "images", label: "Images", icon: ImageIcon },
  { id: "videos", label: "Videos", icon: Video },
  { id: "following", label: "Following", icon: UserCheck },
];

const REALMS = [
  { id: "",          label: "Every realm" },
  { id: "marketing", label: "Marketing" },
  { id: "designer",  label: "Designing" },
  { id: "cgi",       label: "CGI" },
  { id: "movie",     label: "Movie" },
];

const CommunityPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [realm, setRealm]   = useState("");          // "" = every realm
  const [search, setSearch] = useState("");
  const [activeTags, setActiveTags] = useState([]);
  const [trendingTags, setTrendingTags] = useState([]);
  const [posts, setPosts] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openPost, setOpenPost] = useState(null);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [myCreations, setMyCreations] = useState({ images: [], videos: [] });
  const [loadingMine, setLoadingMine] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  // Load trending tags once
  useEffect(() => {
    axios.get(`${API_URL}/api/community/tags/trending?limit=12`)
      .then(r => setTrendingTags(r.data.items || []))
      .catch(() => {});
  }, []);

  const fetchFeed = useCallback(
    async (resetSkip = 0, currentFilter = filter, currentSearch = search, currentTags = activeTags, currentRealm = realm) => {
      if (resetSkip === 0) setLoading(true);
      else setLoadingMore(true);
      try {
        const params = { filter: currentFilter, skip: resetSkip, limit: PAGE_SIZE };
        if (currentSearch && currentSearch.trim()) params.q = currentSearch.trim();
        if (currentTags && currentTags.length) params.tags = currentTags.join(",");
        if (currentRealm) params.realm = currentRealm;
        const r = await axios.get(`${API_URL}/api/community/feed`, {
          params,
          headers: authHeader,
        });
        if (resetSkip === 0) setPosts(r.data.items || []);
        else setPosts((prev) => [...prev, ...(r.data.items || [])]);
        setSkip(resetSkip + (r.data.items || []).length);
        setHasMore(!!r.data.has_more);
      } catch (e) {
        toast.error("Could not load community feed.");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, search, activeTags, realm, token]
  );

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
  useEffect(() => {
    fetchFeed(0, filter, search, activeTags, realm);
  }, [filter, activeTags, realm]);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => fetchFeed(0, filter, search, activeTags, realm), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const toggleTag = (tag) => {
    setActiveTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag].slice(0, 5)
    );
  };

  const handleLike = async (post) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/community/posts/${post.id}/like`,
        {},
        { headers: authHeader }
      );
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? { ...p, liked_by_me: r.data.liked, likes_count: r.data.likes_count }
            : p
        )
      );
    } catch (e) {
      toast.error("Could not update like.");
    }
  };

  const handlePostUpdate = (updated) => {
    if (updated.status === "removed") {
      setPosts((prev) => prev.filter((p) => p.id !== updated.id));
    } else {
      setPosts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    }
  };

  const openShareMenu = async () => {
    setShareMenuOpen(true);
    setLoadingMine(true);
    try {
      const [imgs, vids] = await Promise.all([
        axios.get(`${API_URL}/api/content`, { headers: authHeader }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/video-gen/history`, { headers: authHeader }).catch(() => ({ data: [] })),
      ]);
      const images = (Array.isArray(imgs.data) ? imgs.data : imgs.data?.content || [])
        .filter((c) => c.image_url)
        .slice(0, 24);
      const videos = (Array.isArray(vids.data) ? vids.data : vids.data?.videos || vids.data?.tasks || vids.data?.history || [])
        .filter((v) => v.status === "completed" && (v.polished_url || v.video_url))
        .slice(0, 24);
      setMyCreations({ images, videos });
    } catch (e) {
      toast.error("Could not load your creations.");
    } finally {
      setLoadingMine(false);
    }
  };

  return (
    <div className="space-y-8 px-4 lg:px-0" data-testid="community-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-coral-500/10 border border-coral-500/30 rounded-full mb-3">
            <Users className="w-3 h-3 text-coral-500" />
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-coral-500">
              Community
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-ink-950 tracking-tight">
            Made by creators,
            <br />
            powered by XEA.
          </h1>
          <p className="text-ink-500 mt-3 max-w-xl">
            Share your best work, get inspired, remix prompts from other creators, and follow the
            voices you love.
          </p>
        </div>
        <Button
          onClick={openShareMenu}
          data-testid="community-share-fab"
          className="bg-ink-950 text-white hover:bg-ink-800 self-start md:self-end"
        >
          <Plus className="w-4 h-4" />
          Share a creation
        </Button>
      </div>

      {/* Weekend Top 20 — only renders when active (Sat 00:00 → Sun 12:00) */}
      <WeekendTop20Carousel realm={realm} onOpenPost={(p) => setOpenPost(p)} />

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="community-search"
          placeholder="Search captions, prompts, vibes…"
          className="w-full pl-11 pr-10 py-2.5 rounded-full border border-ink-200 bg-white focus:outline-none focus:border-ink-950 text-sm"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            data-testid="community-search-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-950"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Tag chips (trending) */}
      {trendingTags.length > 0 && (
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="community-trending-tags">
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-400 shrink-0 pr-1">
            Trending
          </span>
          {trendingTags.map((t) => {
            const active = activeTags.includes(t.tag);
            return (
              <button
                key={t.tag}
                type="button"
                onClick={() => toggleTag(t.tag)}
                data-testid={`community-tag-${t.tag}`}
                className={`shrink-0 px-3 py-1 rounded-full text-xs font-bold transition-all ${
                  active
                    ? "bg-coral-500 text-white"
                    : "bg-white border border-ink-200 text-ink-700 hover:border-ink-950"
                }`}
              >
                #{t.tag}
                {active && <X className="w-3 h-3 inline ml-1" />}
              </button>
            );
          })}
          {activeTags.length > 0 && (
            <button
              type="button"
              onClick={() => setActiveTags([])}
              data-testid="community-tags-clear"
              className="shrink-0 text-[10px] uppercase tracking-[0.18em] font-bold text-rose-500 hover:underline"
            >
              clear
            </button>
          )}
        </div>
      )}

      {/* Realm chips — Marketing / Designing / CGI / Movie */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="community-realm-row">
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-500 shrink-0 mr-1">
          Realm
        </span>
        {REALMS.map((r) => {
          const active = realm === r.id;
          return (
            <button
              key={r.id || "all"}
              onClick={() => setRealm(r.id)}
              data-testid={`community-realm-${r.id || "all"}`}
              className={`shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold transition-all ${
                active
                  ? "bg-fuchsia-500 text-white shadow-ambient"
                  : "bg-white border border-ink-200 text-ink-700 hover:border-fuchsia-400"
              }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`community-filter-${f.id}`}
              className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
                active
                  ? "bg-ink-950 text-white shadow-ambient"
                  : "bg-white border border-ink-200 text-ink-700 hover:border-ink-950"
              }`}
            >
              <f.icon className="w-3 h-3" />
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Feed */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-ink-500" />
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-ink-200 p-12 text-center bg-white">
          <Users className="w-12 h-12 mx-auto text-ink-300 mb-4" />
          <h3 className="text-lg font-bold text-ink-950 mb-2">
            {filter === "following" ? "Follow creators to fill this feed" : "Be the first to share"}
          </h3>
          <p className="text-sm text-ink-500 max-w-md mx-auto mb-6">
            {filter === "following"
              ? "Follow creators whose style you love and their posts will land here."
              : "The community is just getting started. Share one of your generations and inspire others."}
          </p>
          <Button onClick={openShareMenu} className="bg-ink-950 text-white">
            <Plus className="w-4 h-4" />
            Share my first post
          </Button>
        </div>
      ) : (
        <>
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
            {posts.map((p) => (
              <CommunityCard
                key={p.id}
                post={p}
                onOpen={(post) => setOpenPost(post)}
                onLike={handleLike}
              />
            ))}
          </div>
          {hasMore && (
            <div className="flex justify-center py-6">
              <Button
                variant="outline"
                onClick={() => fetchFeed(skip, filter)}
                disabled={loadingMore}
                data-testid="community-load-more"
              >
                {loadingMore && <Loader2 className="w-3 h-3 animate-spin" />}
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      {openPost && (
        <PostDetailModal
          post={openPost}
          onClose={() => setOpenPost(null)}
          onUpdate={handlePostUpdate}
        />
      )}

      {/* Pick-from-my-creations share menu */}
      {shareMenuOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/70 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => setShareMenuOpen(false)}
          data-testid="community-share-picker"
        >
          <div
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-2xl font-black text-ink-950">Pick a creation to share</h3>
                <p className="text-sm text-ink-500 mt-1">
                  Choose from your recent images and videos. You can add a caption next.
                </p>
              </div>
              <button
                onClick={() => setShareMenuOpen(false)}
                className="p-2 hover:bg-ink-100 rounded-full text-ink-500"
                data-testid="community-share-picker-close"
              >
                ✕
              </button>
            </div>

            {loadingMine ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-ink-500" />
              </div>
            ) : myCreations.images.length === 0 && myCreations.videos.length === 0 ? (
              <div className="text-center py-12">
                <Sparkles className="w-10 h-10 mx-auto text-ink-300 mb-3" />
                <p className="text-sm text-ink-500 mb-4">
                  You haven&apos;t generated anything yet. Create your first image or video.
                </p>
                <div className="flex gap-2 justify-center">
                  <Button onClick={() => navigate("/dashboard/designer")} variant="outline">
                    Generate image
                  </Button>
                  <Button
                    onClick={() => navigate("/dashboard/ai-video")}
                    className="bg-ink-950 text-white"
                  >
                    Generate video
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {myCreations.images.length > 0 && (
                  <div>
                    <h4 className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
                      <ImageIcon className="w-3 h-3 inline mr-1" /> Images
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {myCreations.images.map((c) => (
                        <div
                          key={c.id}
                          className="rounded-2xl overflow-hidden border border-ink-200 bg-ink-50 group relative"
                          data-testid={`community-pick-image-${c.id}`}
                        >
                          <img
                            src={c.image_url}
                            alt=""
                            className="w-full aspect-square object-cover"
                          />
                          <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-ink-950/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <ShareToCommunityButton
                              sourceCollection="content"
                              sourceId={c.id}
                              size="sm"
                              variant="default"
                              className="w-full bg-white text-ink-950"
                              testId={`community-pick-image-share-${c.id}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {myCreations.videos.length > 0 && (
                  <div>
                    <h4 className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
                      <Video className="w-3 h-3 inline mr-1" /> Videos
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {myCreations.videos.map((v) => (
                        <div
                          key={v.id}
                          className="rounded-2xl overflow-hidden border border-ink-200 bg-ink-50 group relative"
                          data-testid={`community-pick-video-${v.id}`}
                        >
                          <video
                            src={v.polished_url || v.video_url}
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full aspect-[9/16] object-cover"
                          />
                          <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-ink-950/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <ShareToCommunityButton
                              sourceCollection="video_tasks"
                              sourceId={v.id}
                              size="sm"
                              variant="default"
                              className="w-full bg-white text-ink-950"
                              testId={`community-pick-video-share-${v.id}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 text-center">
              <Link
                to="/dashboard/community/me"
                onClick={() => setShareMenuOpen(false)}
                className="text-xs text-ink-500 hover:text-ink-950 underline"
              >
                Manage my community posts →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityPage;
